/**
 * Singleton bootstrap + safe-by-default helpers for the add-in telemetry
 * client. Two design goals:
 *
 *   1. Call sites stay terse. A user-action handler can call
 *      `logEvent("skill.invoke", "usage", { skillId })` without importing
 *      the client class or wiring up dependencies.
 *
 *   2. Logging is safe before bootstrap. If `initTelemetry` was never
 *      called (because the host is a test, or the page is the auth dialog
 *      that runs in a different surface), the helpers are silent no-ops
 *      instead of throwing. Tests don't need to mock the module.
 *
 * The actual {@link TelemetryClient} construction is deferred until
 * `initTelemetry` is invoked from `main.tsx` once Office.onReady fires and
 * the bearer-token provider is wired.
 */

import {
  HttpTelemetryTransport,
  TelemetryClient,
  createOutbox,
  installCaptureHooks,
} from "./index";
import type {
  EventKind,
  EventName,
  TelemetryError,
  TelemetryIdentity,
  TelemetryProperties,
} from "./types";

let client: TelemetryClient | undefined;
let cleanupCaptureHooks: (() => void) | undefined;

export interface InitTelemetryOptions {
  /** Absolute URL of the Telemetry.Endpoints POST endpoint. */
  endpoint: string;
  /** Resolve a fresh bearer token. Called per batch send. */
  getBearerToken: () => Promise<string | null>;
  /**
   * Silently re-acquire a fresh bearer after a send 401s. Called once per failed
   * send; when it yields a token the POST is retried with it. Omitted in tests /
   * before auth wires the refresher.
   */
  refreshBearerToken?: () => Promise<string | null>;
  /**
   * Synchronous accessor for the current beacon ticket, wired into the
   * transport's `sendBeacon` (pagehide) path. `sendBeacon` cannot await, so
   * this must return a cached value synchronously (see `beaconTicket.ts`).
   * Omitted in tests / before the ticket manager is wired - the beacon path
   * then stays disabled and events flush via the authenticated POST instead.
   */
  getBeaconTicket?: () => string | null;
  /** Initial identity bundle. May be partial; rebound later via {@link bindIdentity}. */
  identity?: Partial<TelemetryIdentity>;
  /** When true, install global window error / unhandledrejection hooks. Default true. */
  installCaptureHooks?: boolean;
}

/**
 * Live bearer token for telemetry sends. Set once auth settles (see
 * `AuthStateProvider`) so the transport's `getBearerToken` returns a real
 * token instead of the boot-time `null`. Mirrors how the addin-api call
 * receives the id-token from the auth layer - but via a module-level holder
 * because the telemetry client is constructed at boot, outside React.
 */
let liveBearerToken: string | null = null;

/** Update the bearer token used for telemetry sends. Safe to call repeatedly. */
export function setTelemetryAuthToken(token: string | null): void {
  liveBearerToken = token;
}

/** Read the current telemetry bearer token. Used by the boot-time provider. */
export function getTelemetryAuthToken(): string | null {
  return liveBearerToken;
}

/**
 * Silently re-acquire a fresh telemetry bearer, set once auth settles (see
 * `AuthStateProvider`). The transport calls this when a send 401s so a stale
 * idToken is renewed and the batch retried instead of dropped. Module-level for
 * the same boot-vs-React reason as {@link liveBearerToken}.
 */
let liveTokenRefresher: (() => Promise<string | null>) | null = null;

/** Set (or clear with `null`) the telemetry bearer refresher. */
export function setTelemetryTokenRefresher(
  refresher: (() => Promise<string | null>) | null,
): void {
  liveTokenRefresher = refresher;
}

/** Read the current telemetry bearer refresher, or `null` if none is wired. */
export function getTelemetryTokenRefresher(): (() => Promise<string | null>) | null {
  return liveTokenRefresher;
}

/**
 * Construct (or reset) the singleton {@link TelemetryClient}, install global
 * capture hooks, and return the client. Safe to call multiple times - the
 * previous instance is discarded and its capture hooks removed first.
 */
export function initTelemetry(options: InitTelemetryOptions): TelemetryClient {
  // Tear down the previous instance if this is a re-init (test scenario).
  cleanupCaptureHooks?.();
  cleanupCaptureHooks = undefined;

  const outbox = createOutbox();
  const transport = new HttpTelemetryTransport({
    endpoint: options.endpoint,
    getBearerToken: options.getBearerToken,
    refreshBearerToken: options.refreshBearerToken,
    // The transport's BeaconTicketProvider is typed as async, but `sendBeacon`
    // only accepts a synchronously-resolved string (it treats a returned
    // Promise as "unconfigured"). We pass the sync accessor through under that
    // type so the cached ticket is read without awaiting on pagehide.
    getBeaconTicket: options.getBeaconTicket as
      | (() => Promise<string | null>)
      | undefined,
  });
  client = new TelemetryClient({ outbox, transport });

  if (options.identity) {
    client.bindIdentity(options.identity);
  }

  if (options.installCaptureHooks !== false) {
    cleanupCaptureHooks = installCaptureHooks(client);
  }

  return client;
}

/** Returns the singleton client, or `undefined` if not yet initialised. */
export function getTelemetry(): TelemetryClient | undefined {
  return client;
}

/**
 * Update the bound identity. Call this once `/api/<channel>/settings` has
 * resolved the user, tenant, persona, and Office.js diagnostics.
 */
export function bindIdentity(identity: Partial<TelemetryIdentity>): void {
  client?.bindIdentity(identity);
}

/**
 * Tear down the singleton - clears the queue, removes capture hooks, releases
 * the reference. Tests use this; production never does.
 */
export async function resetTelemetry(): Promise<void> {
  cleanupCaptureHooks?.();
  cleanupCaptureHooks = undefined;
  if (client) {
    await client._reset();
    client = undefined;
  }
}

// -- Safe-by-default logging helpers --

/**
 * Log a non-error event. Safe no-op when telemetry is not initialised.
 * @param name Closed-catalogue event name (TypeScript enforces this).
 * @param kind `usage`, `performance`, `info`, or `warn`.
 * @param properties Allow-listed property bag (TypeScript enforces keys).
 */
export function logEvent(
  name: EventName,
  kind: Exclude<EventKind, "error">,
  properties?: TelemetryProperties,
): void {
  client?.enqueue(name, kind, properties);
}

/**
 * Log an error event. Safe no-op when telemetry is not initialised. The
 * caller is responsible for pre-scrubbing the error message - see
 * `scrubMessage` in the `scrub` module.
 */
export function logError(
  name: EventName,
  error: TelemetryError,
  properties?: TelemetryProperties,
): void {
  client?.enqueueError(name, error, properties);
}

/**
 * Trigger a flush. Used by the `pagehide` and `online` listeners wired in
 * `main.tsx`. Safe no-op when telemetry is not initialised.
 */
export async function flushTelemetry(
  reason: "unload" | "reconnect" | "manual" = "manual",
): Promise<void> {
  await client?.flush(reason);
}
